import React, { useMemo, useState } from 'react';
import { X, Search } from 'lucide-react';
import Button from './Button';
import { useCreateTicket, type CreateTicketPayload } from '../hooks/useTickets';
import {
  usePortalBranches,
  usePortalRooms,
  usePortalSlots,
  useBookingSearch,
  type BookingSearchResult,
} from '../hooks/usePortalWorkflow';

interface CreateTicketModalProps {
  open: boolean;
  onClose: () => void;
}

function Field({ label, className = '', children }: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

function defaultSessionDateTimeLocal(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T12:00`;
}

function toDatetimeLocalValue(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

const CreateTicketModal: React.FC<CreateTicketModalProps> = ({ open, onClose }) => {
  const createMutation = useCreateTicket();
  const { data: branches = [] } = usePortalBranches();

  const [branchId, setBranchId] = useState(0);
  const [roomId, setRoomId] = useState(0);
  const [sessionDateTime, setSessionDateTime] = useState(defaultSessionDateTimeLocal);
  const [players, setPlayers] = useState(2);
  const [selectedSlot, setSelectedSlot] = useState<{ startTime: string; eventId: string } | null>(null);
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState('en');
  const [bookingId, setBookingId] = useState<number | undefined>();
  const [linkedBookingLabel, setLinkedBookingLabel] = useState<string | null>(null);

  const [bookingRef, setBookingRef] = useState('');
  const [bookingPhone, setBookingPhone] = useState('');
  const [runBookingSearch, setRunBookingSearch] = useState(false);

  const slotDate = sessionDateTime.slice(0, 10);
  const { data: rooms = [] } = usePortalRooms(branchId || undefined);
  const { data: slots = [], isLoading: slotsLoading } = usePortalSlots(roomId || undefined, slotDate, players);

  const searchByRefOnly = !!bookingRef.trim() && !bookingPhone.trim();
  const { data: bookingResults = [], isFetching: searchingBookings } = useBookingSearch(
    {
      bookingRef: bookingRef || undefined,
      phone: bookingPhone || undefined,
      branchId: branchId || undefined,
      date: searchByRefOnly ? undefined : slotDate,
    },
    runBookingSearch && (!!bookingRef.trim() || !!bookingPhone.trim())
  );

  const sessionIso = useMemo(
    () => selectedSlot?.startTime || fromDatetimeLocalValue(sessionDateTime),
    [selectedSlot, sessionDateTime]
  );

  const hasSessionAnchor = !!bookingId || !!sessionIso;
  const canSubmit = branchId > 0 && roomId > 0 && !!title.trim() && hasSessionAnchor;

  const applyBooking = (b: BookingSearchResult) => {
    setBranchId(b.branchId);
    setRoomId(b.roomId);
    if (b.slotStartAt) {
      setSessionDateTime(toDatetimeLocalValue(b.slotStartAt) || sessionDateTime);
      setSelectedSlot({ startTime: b.slotStartAt, eventId: '' });
    }
    setBookingId(b.id);
    setLinkedBookingLabel(b.bookingRef ? `#${b.bookingRef}` : `Booking ${b.id}`);
    if (b.customerName) setCustomerName(b.customerName);
    if (b.customerPhone) setCustomerPhone(b.customerPhone);
    if (b.preferredLanguage) setPreferredLanguage(b.preferredLanguage);
    if (!title.trim() && b.roomName) setTitle(`Recording — ${b.roomName}`);
    setRunBookingSearch(false);
  };

  const clearLinkedBooking = () => {
    setBookingId(undefined);
    setLinkedBookingLabel(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    const payload: CreateTicketPayload = {
      branchId,
      roomId,
      title: title.trim(),
      notes: notes.trim() || undefined,
      slotStartAt: sessionIso,
      bookingId,
      bookeoEventId: selectedSlot?.eventId || undefined,
      customerName: customerName.trim() || undefined,
      customerPhone: customerPhone.trim() || undefined,
      preferredLanguage: preferredLanguage || 'en',
    };

    try {
      await createMutation.mutateAsync(payload);
      onClose();
    } catch {
      // Error toast handled by useCreateTicket
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        <ModalHeader onClose={onClose} />
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <section className="space-y-3 border-b border-gray-200 pb-4">
            <h3 className="text-sm font-semibold text-gray-900">Find booking (optional)</h3>
            <p className="text-xs text-gray-500">
              Link a past or upcoming booking by reference or phone. You can also skip this and set the session time
              below (e.g. retroactive video from stream history).
            </p>
            <div className="flex flex-wrap gap-2">
              <input
                type="text"
                placeholder="Booking ref"
                value={bookingRef}
                onChange={(e) => setBookingRef(e.target.value)}
                className="etr-input flex-1 min-w-[120px]"
              />
              <input
                type="text"
                placeholder="Customer phone"
                value={bookingPhone}
                onChange={(e) => setBookingPhone(e.target.value)}
                className="etr-input flex-1 min-w-[120px]"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                leftIcon={<Search className="h-4 w-4" />}
                isLoading={searchingBookings}
                onClick={() => setRunBookingSearch(true)}
              >
                Search
              </Button>
            </div>
            {runBookingSearch && !searchingBookings && bookingResults.length === 0 && (
              <p className="text-sm text-gray-500">No bookings found. Enter session time manually below.</p>
            )}
            {bookingResults.length > 0 && (
              <ul className="max-h-32 overflow-y-auto border border-gray-200 rounded-md divide-y">
                {bookingResults.map((b) => (
                  <li key={b.id}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                      onClick={() => applyBooking(b)}
                    >
                      <span className="font-medium">{b.roomName ?? `Room ${b.roomId}`}</span>
                      {' · '}
                      {new Date(b.slotStartAt).toLocaleString()}
                      {b.customerName ? ` · ${b.customerName}` : ''}
                      {b.bookingRef ? ` · ${b.bookingRef}` : ''}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {linkedBookingLabel && (
              <p className="text-sm text-green-800 flex items-center gap-2">
                Linked booking {linkedBookingLabel}
                <button type="button" className="text-xs underline text-gray-600" onClick={clearLinkedBooking}>
                  Clear
                </button>
              </p>
            )}
          </section>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Branch *">
              <select
                className="etr-input w-full"
                value={branchId}
                onChange={(e) => {
                  setBranchId(Number(e.target.value));
                  setRoomId(0);
                  setSelectedSlot(null);
                  clearLinkedBooking();
                }}
                required
              >
                <option value={0}>Select branch</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Room *">
              <select
                className="etr-input w-full"
                value={roomId}
                onChange={(e) => {
                  setRoomId(Number(e.target.value));
                  setSelectedSlot(null);
                }}
                required
                disabled={!branchId}
              >
                <option value={0}>Select room</option>
                {rooms.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Session date & time *" className="sm:col-span-2">
              <input
                type="datetime-local"
                className="etr-input w-full"
                value={sessionDateTime}
                onChange={(e) => {
                  setSessionDateTime(e.target.value);
                  setSelectedSlot(null);
                }}
                required={!bookingId}
              />
              <p className="mt-1 text-xs text-gray-500">
                When the customer played (past or today). Required unless a booking is linked above.
              </p>
            </Field>
            <Field label="Players (for live slot lookup)">
              <input
                type="number"
                min={1}
                max={12}
                className="etr-input w-full"
                value={players}
                onChange={(e) => setPlayers(Number(e.target.value) || 2)}
              />
            </Field>
          </div>

          {roomId > 0 && (
            <Field label="Live Bookeo slots (optional)">
              {slotsLoading ? (
                <p className="text-sm text-gray-500">Loading available slots for this day…</p>
              ) : slots.length === 0 ? (
                <p className="text-sm text-gray-600">
                  No live slots listed for this day — you can still create the ticket using the session time above
                  (e.g. record from stream history after the visit).
                </p>
              ) : (
                <select
                  className="etr-input w-full"
                  value={selectedSlot?.startTime ?? ''}
                  onChange={(e) => {
                    const slot = slots.find((s) => s.startTime === e.target.value);
                    if (slot) {
                      setSelectedSlot({ startTime: slot.startTime, eventId: slot.eventId });
                      setSessionDateTime(toDatetimeLocalValue(slot.startTime));
                    } else {
                      setSelectedSlot(null);
                    }
                  }}
                >
                  <option value="">Use session time above only</option>
                  {slots.map((s) => (
                    <option key={s.eventId || s.startTime} value={s.startTime}>
                      {new Date(s.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {s.endTime
                        ? ` – ${new Date(s.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                        : ''}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}

          <Field label="Title *">
            <input className="etr-input w-full" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </Field>
          <Field label="Notes">
            <textarea className="etr-input w-full" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          <div className="flex flex-wrap gap-2">
            <Field label="Customer name" className="flex-1 min-w-[140px]">
              <input
                className="etr-input w-full"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </Field>
            <Field label="Customer phone" className="flex-1 min-w-[140px]">
              <input
                className="etr-input w-full"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
              />
            </Field>
            <Field label="Lang" className="w-28">
              <select
                className="etr-input w-full"
                value={preferredLanguage}
                onChange={(e) => setPreferredLanguage(e.target.value)}
              >
                <option value="en">EN</option>
                <option value="ar">AR</option>
              </select>
            </Field>
          </div>

          <ModalFooter onClose={onClose} canSubmit={canSubmit} isLoading={createMutation.isLoading} />
        </form>
      </div>
    </div>
  );
};

function ModalHeader({ onClose }: { onClose: () => void }) {
  return (
    <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
      <h2 className="text-xl font-bold text-gray-900">New recording ticket</h2>
      <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}

function ModalFooter({
  onClose,
  canSubmit,
  isLoading,
}: {
  onClose: () => void;
  canSubmit: boolean;
  isLoading: boolean;
}) {
  return (
    <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
      <Button type="button" variant="outline" onClick={onClose}>
        Cancel
      </Button>
      <Button type="submit" variant="primary" isLoading={isLoading} disabled={!canSubmit}>
        Create ticket
      </Button>
    </div>
  );
}

export default CreateTicketModal;
